/**
 * 加密工具 crypto
 */
import CryptoJS from 'crypto-js'

/**
 * 默认 AES 参数
 * 注意：AES 要求 key 长度必须是 16 / 24 / 32 字节，IV 必须 16 字节
 *
 * ⚠️ 安全警告：
 * 这些密钥硬编码在客户端代码中，仅用于客户端到服务端的传输加密。
 * 客户端加密无法真正保护数据安全，因为密钥对所有人可见。
 *
 * 建议：
 * 1. 如果可能，使用 HTTPS 传输，由服务端处理敏感数据加密
 * 2. 如果需要客户端加密，考虑使用服务端提供的临时密钥
 * 3. 不要使用这些密钥加密真正的敏感数据
 *
 * 生产环境建议：
 * - 将密钥移到服务端配置
 * - 或使用服务端提供的加密方案
 */
export const DEFAULT_KEY = 'k8s3m9p2q6r1t4w7'
export const DEFAULT_IV = 'x5z2y9v0u8n4b7c3'

export interface CryptoOptions {
  key?: string
  iv?: string
}

/**
 * AES 加密（CBC + PKCS7）
 */
export function encryptData(
  data: unknown,
  options: CryptoOptions = {}
): string {
  const { key = DEFAULT_KEY, iv = DEFAULT_IV } = options

  const dataStr = typeof data === 'string' ? data : JSON.stringify(data)

  const encrypted = CryptoJS.AES.encrypt(
    CryptoJS.enc.Utf8.parse(dataStr),
    CryptoJS.enc.Utf8.parse(key),
    {
      iv: CryptoJS.enc.Utf8.parse(iv),
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    }
  )

  return encrypted.ciphertext.toString(CryptoJS.enc.Base64)
}

/**
 * AES 解密（CBC + PKCS7）
 */
export function decryptData<T = unknown>(
  encryptedData: string,
  options: CryptoOptions = {}
): T {
  const { key = DEFAULT_KEY, iv = DEFAULT_IV } = options

  // 空字符串或空白内容，直接返回空结果，避免无意义的解密错误
  if (!encryptedData || encryptedData.trim() === '') {
    return '' as unknown as T
  }

  try {
    // Base64 → CryptoJS 格式
    const encryptedHex = CryptoJS.enc.Base64.parse(encryptedData)
    const encryptedBase64 = CryptoJS.enc.Base64.stringify(encryptedHex)

    const decrypted = CryptoJS.AES.decrypt(
      encryptedBase64,
      CryptoJS.enc.Utf8.parse(key),
      {
        iv: CryptoJS.enc.Utf8.parse(iv),
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    )

    const decryptedStr = decrypted.toString(CryptoJS.enc.Utf8)

    // 解密结果为空，说明数据可能不是使用约定 key/iv 加密的
    // 这里不抛错，交给调用方自行决定如何处理（通常会有兜底逻辑）
    if (!decryptedStr) {
      return '' as unknown as T
    }

    // 解析 JSON（如果能解析）
    try {
      return JSON.parse(decryptedStr)
    } catch {
      return decryptedStr as unknown as T
    }
  } catch (error) {
    // 不在工具函数内部打印错误日志，由调用方决定是否记录日志
    // 保留原始错误信息，方便上层捕获和处理
    if (error instanceof Error) {
      throw error
    }
    throw new Error(String(error))
  }
}

/**
 * 生成随机 AES Key（16 / 24 / 32 字节）
 */
export function generateKey(length: 16 | 24 | 32 = 16): string {
  return CryptoJS.lib.WordArray.random(length).toString(CryptoJS.enc.Hex).slice(0, length)
}

/**
 * 生成随机 16 字节 IV
 */
export function generateIv(): string {
  return CryptoJS.lib.WordArray.random(16).toString(CryptoJS.enc.Hex).slice(0, 16)
}
